import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { StepProps } from '@lumapps/lumx-layouts/types';
import { mdiEmailOutline } from '@lumapps/lumx/icons';
import { Icon, Size, Link, Text, Typography } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import './index.scss';
import { SIGN_IN_LINK_VALIDITY_IN_MINUTES } from '../../constants';
import { SIGN_IN_CORE } from '../keys';

const CLASSNAME = 'check-emails-page';

export interface CheckEmailsPageProps extends StepProps {
    /** sent link message, displayed above the address the link was sent to */
    sentLinkMessage?: string;
    /**
     * complete sentence naming the address the sign-in link was sent to, displayed with the link validity
     * instead of `sentLinkMessage`
     */
    message?: React.ReactNode;
    /** whether the link going back to the address input is displayed */
    hasTryAnotherAddress?: boolean;
}
export const CheckEmailsPage: React.FC<CheckEmailsPageProps> = ({
    goToPreviousStep,
    state,
    sentLinkMessage,
    message,
    hasTryAnotherAddress = true,
}) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const { element, block } = useClassnames(CLASSNAME);

    return (
        <div className={block()}>
            <div className={element('icon-wrapper')}>
                <Icon className={element('icon')} icon={mdiEmailOutline} size={Size.xl} hasShape />
            </div>

            {message ? (
                <div className={element('email-block')}>
                    <Text as="p">{message}</Text>

                    <Text as="p">
                        {translateAndReplace(SIGN_IN_CORE.LINK_VALIDITY_DURATION, {
                            MINUTES: SIGN_IN_LINK_VALIDITY_IN_MINUTES,
                        })}
                    </Text>
                </div>
            ) : (
                state !== undefined &&
                sentLinkMessage && (
                    <div className={element('email-block')}>
                        <Text as="p">{sentLinkMessage}</Text>

                        <Text as="p" typography={Typography.subtitle1}>
                            {state.email}
                        </Text>
                    </div>
                )
            )}

            {hasTryAnotherAddress && (
                <div className={element('reset-password-link')}>
                    <Text as="p" typography={Typography.body2}>
                        {translateKey(SIGN_IN_CORE.EMAIL_NOT_RECEIVED)}
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link className={element('link-text')} onClick={goToPreviousStep}>
                            {translateKey(SIGN_IN_CORE.TRY_ANOTHER_ADDRESS)}
                        </Link>
                    </Text>
                </div>
            )}
        </div>
    );
};
